import { CurrencyPipe, DatePipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AddOn } from 'src/app/pages/new-billing/models/payment.model';
import { CapitalizePipe } from 'src/app/pipes/capitalize/capitalize.pipe';
import { BillingService } from 'src/app/services/billing/billing.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { PaddleService } from 'src/app/services/paddle/service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { convertCentsToDollars, normalizePrice } from 'src/app/util/price-formatter.util';
import { CheckoutComponent, CheckoutDialogData } from '../checkout/checkout.component';

// cached addon data with normalized props
type CachedAddOn = Pick<AddOn, 'name' | 'shortDescription'> & {
  totalPrice: number;
};

@Component({
  selector: 'app-checkout-v2',
  templateUrl: './checkout-v2.component.html',
  styleUrls: ['./checkout-v2.component.scss'],
  providers: [CapitalizePipe, DatePipe, CurrencyPipe, LowerCasePipe],
})
export class CheckoutV2Component extends CheckoutComponent implements OnInit, OnDestroy {

  private _cachedAddons: CachedAddOn[] | null = null;
  private _cachedAddonsKey: string | null = null;
  private _addons: CachedAddOn[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public override dialogData: CheckoutDialogData,
    public override paymentProvider: PaddleService,
    protected billingService: BillingService,
    protected dialogRef: MatDialogRef<CheckoutComponent>,
    protected segment: SegmentService,
    protected store: Store,
    protected changeDetectorRef: ChangeDetectorRef,
    protected growthBook: GrowthBookService,
    private readonly capitalize: CapitalizePipe,
    private readonly currencyPipe: CurrencyPipe,
    private readonly datePipe: DatePipe,
    private readonly lowerCasePipe: LowerCasePipe,
    translate: TranslateService,
    protected readonly localizedPricingService: LocalizedPricingService,
  ) {
    super(
      { ...dialogData, isV2: true },
      paymentProvider,
      billingService,
      dialogRef,
      segment,
      store,
      changeDetectorRef,
      growthBook,
      localizedPricingService,
      translate,
    );
  }

  /** Trial strip in v2 dialog; default true unless dialog sets `showTrialHeadsUpBanner: false`. */
  get showTrialHeadsUpBanner(): boolean {
    return this.dialogData.showTrialHeadsUpBanner !== false;
  }

  get addons() {
    return this._addons;
  }

  private updateAddonsCache(): void {
    if (!this.currentPlan.paymentModel.addOns) {
      this._addons = [];
      return;
    }

    const cacheKey = this.createCacheKey();

    // Return cached result if available and data hasn't changed
    if (this._cachedAddons && this._cachedAddonsKey === cacheKey) {
      this._addons = this._cachedAddons;
      return;
    }

    const result = this.processAddons();

    // Cache the result
    this._cachedAddons = result;
    this._cachedAddonsKey = cacheKey;
    this._addons = result;
  }

  private createCacheKey(): string {
    // Use consistent quantity source to avoid cache key mismatches
    const quantity = this.getConsistentQuantity();

    return JSON.stringify({
      addOns: this.currentPlan.paymentModel.addOns,
      quantity,
      recurrence: this.currentPlan?.paymentModel?.paymentRecurrence,
    });
  }

  private getConsistentQuantity(): number {
    // Prioritize usersCount as it's the primary source, fallback to API data
    return this.currentPlan.usersCount ||
           this.currentPlan?.billingApiData?.billingDetails?.quantity ||
           0;
  }

  private processAddons(): CachedAddOn[] {
    const quantity = this.getConsistentQuantity();
    const planRecurrence = this.getPlanRecurrence();

    return this.currentPlan.paymentModel.addOns.map(addon => {
      const name = addon?.name ?? '';
      const shortDescription = this.processShortDescription(addon?.shortDescription ?? '', name, addon);
      const totalPrice = this.calculateTotalPrice(addon, quantity, planRecurrence);

      return {
        name,
        shortDescription,
        totalPrice,
      };
    });
  }

  private processShortDescription(description: string, name: string, addon: AddOn): string {
    if (!description) return '';

    // Remove all occurrences of the name from the description
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const nameRegex = new RegExp(escapedName, 'gi');

    let processedDescription = description
      .replace(nameRegex, '')
      .replace(/\bmonth\b/g, 'mo')
      .replace(/\bmonthly\b/g, 'mo')
      .replace(/\byear\b/g, 'yr')
      .replace(/\byearly\b/g, 'yr')
      .replace(/\bper \b/g, '/')
      .replace(/\bper\b/g, '/')
      .replace(/\bYearly\b/g, '')
      .replace(/-/g, '')
      .replace(/\bYearly\b/g, '');

    // Handle currency localization in the description
    // Look for patterns like "$36 /user/yr" or "$2 /user/mo"
    const currencyPattern = /\$(\d+(?:\.\d{2})?)\s*\/user\/(?:mo|yr)/g;
    const matches = processedDescription.match(currencyPattern);

    if (matches) {
      matches.forEach(match => {
        // Extract the dollar amount using string methods to avoid RegExp.exec warning
        const dollarIndex = match.indexOf('$');
        const spaceIndex = match.indexOf(' ');
        if (dollarIndex !== -1 && spaceIndex !== -1) {
          const priceString = match.substring(dollarIndex + 1, spaceIndex);
          const priceInDollars = parseFloat(priceString);
          if (!isNaN(priceInDollars)) {
            // Get the localized price using price overrides
            const localizedPrice = this.getLocalizedPriceForDescription(priceInDollars, addon);
            // Replace the dollar amount with localized version
            processedDescription =  match.replace(`$${priceString}`, localizedPrice);
          }
        }
      });
    }

    return processedDescription;
  }

  /**
   * Gets the localized price for addon description using price overrides
   * @param defaultPriceInDollars - The default price in dollars from the description
   * @param addon - The addon object containing price overrides
   * @returns string - Formatted price with appropriate currency
   */
  private getLocalizedPriceForDescription(defaultPriceInDollars: number, addon: AddOn): string {
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Always respect billing currency if available and has matching price override
    if (billingCurrency && addon?.priceOverrides?.length) {
      const matchingPriceOverride = addon.priceOverrides.find(
        override => override.currencyCode === billingCurrency,
      );

      if (matchingPriceOverride) {
        // Use the localized unit price from the price override (convert from cents to dollars)
        const localizedUnitPrice = convertCentsToDollars(parseFloat(matchingPriceOverride.unitPrice));
        return this.localizedPricingService.formatPriceWithCurrency(localizedUnitPrice, billingCurrency, 'excludePlanAndAddon');
      }
    }

    // Priority 2: Use geo-based pricing only if localized pricing is enabled and no billing currency
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && addon?.priceOverrides?.length) {
      // Try to get geo-based pricing for addon
      const geoBasedPrice = this.getGeoBasedPriceForDescription(addon);
      if (geoBasedPrice) {
        return this.localizedPricingService.formatPriceWithCurrency(geoBasedPrice.price, geoBasedPrice.currency, 'excludePlanAndAddon');
      }
    }

    // Priority 3: Fallback to default USD pricing
    return this.localizedPricingService.formatPriceWithCurrency(defaultPriceInDollars, 'USD', 'excludePlanAndAddon');
  }

  /**
   * Gets geo-based pricing for addon description
   * @param addon - The addon object containing price overrides
   * @returns Object with currency and price, or null if not found
   */
  private getGeoBasedPriceForDescription(addon: AddOn): {currency: string, price: number} | null {
    try {
      // Get cached geo info synchronously
      const cachedGeoInfo = this.localizedPricingService.getCachedGeoInfo();
      const userCountryCode = cachedGeoInfo?.geo?.country;

      if (userCountryCode && addon?.priceOverrides?.length) {
        // Find price override that matches user's country code
        const matchingOverride = addon.priceOverrides.find(
          override => override.countryCode?.toUpperCase() === userCountryCode.toUpperCase(),
        );

        if (matchingOverride?.currencyCode && matchingOverride?.unitPrice) {
          return {
            currency: matchingOverride.currencyCode,
            price: convertCentsToDollars(parseFloat(matchingOverride.unitPrice)),
          };
        }
      }
    } catch (error) {
      // Error getting geo-based price for addon description
    }

    return null;
  }

  /**
   * Calculates the total price for an addon with proper localized pricing support
   * Follows the same priority system as main plan pricing:
   * 1. Billing currency with price overrides
   * 2. Geo-based pricing (if enabled)
   * 3. Default USD pricing
   */
  private calculateTotalPrice(addon: AddOn, quantity: number, planRecurrence: string): number {
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Always respect billing currency if available and has matching price override
    const localizedPrice = this.getLocalizedAddonPrice(addon, billingCurrency, quantity, planRecurrence);
    if (localizedPrice !== null) {
      return localizedPrice;
    }

    // Priority 2: Use geo-based pricing only if localized pricing is enabled and no billing currency
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && this.currentPlan) {
      // Try to get geo-based pricing for addon
      const geoBasedPrice = this.getGeoBasedAddonPrice(addon, quantity, planRecurrence);
      if (geoBasedPrice !== null) {
        return geoBasedPrice;
      }
    }

    // Priority 3: Fallback to default USD pricing
    return this.calculateDefaultAddonPrice(addon, quantity, planRecurrence);
  }

  private getLocalizedAddonPrice(addon: AddOn, billingCurrency: string, quantity: number, planRecurrence: string): number | null {
    if (!billingCurrency || !addon?.priceOverrides?.length) {
      return null;
    }

    const matchingPriceOverride = addon.priceOverrides.find(
      override => override.currencyCode === billingCurrency,
    );

    if (!matchingPriceOverride) {
      return null;
    }

    // Use the localized unit price from the price override (convert from cents to dollars)
    const localizedUnitPrice = convertCentsToDollars(parseFloat(matchingPriceOverride.unitPrice));
    return this.calculateAddonPriceWithQuantity(localizedUnitPrice, addon, quantity, planRecurrence);
  }

  private getGeoBasedAddonPrice(addon: AddOn, quantity: number, planRecurrence: string): number | null {
    try {
      // Get cached geo info synchronously
      const cachedGeoInfo = this.localizedPricingService.getCachedGeoInfo();
      const userCountryCode = cachedGeoInfo?.geo?.country;

      if (userCountryCode && addon?.priceOverrides?.length) {
        // Find price override that matches user's country code
        const matchingOverride = addon.priceOverrides.find(
          override => override.countryCode?.toUpperCase() === userCountryCode.toUpperCase(),
        );

        if (matchingOverride?.currencyCode && matchingOverride?.unitPrice) {
          // Use the localized unit price from the price override (convert from cents to dollars)
          const localizedUnitPrice = convertCentsToDollars(parseFloat(matchingOverride.unitPrice));
          return this.calculateAddonPriceWithQuantity(localizedUnitPrice, addon, quantity, planRecurrence);
        }
      }
    } catch (error) {
      // Error getting geo-based addon price
    }

    return null;
  }

  private calculateDefaultAddonPrice(addon: AddOn, quantity: number, planRecurrence: string): number {
    const unitPrice = addon?.unitPrice || 0;
    return this.calculateAddonPriceWithQuantity(unitPrice, addon, quantity, planRecurrence);
  }

  private calculateAddonPriceWithQuantity(unitPrice: number, addon: AddOn, quantity: number, planRecurrence: string): number {
    const isPerAccount = addon?.isPerAccount;

    if (unitPrice && !isPerAccount) {
      const basePrice = unitPrice * quantity;
      return planRecurrence === 'year' && addon?.billingPeriod === 'monthly'
        ? basePrice * 12
        : basePrice;
    }

    return unitPrice;
  }

  private getPlanRecurrence(): string {
    return this.dialogData.planId?.includes('yearly') ? 'year' : 'month';
  }

  get annualBillingDiscount(): number {
    return this.currentPlan.paymentModel.annualPayDiscount;
  }

  get expiresTrial(): string {
    return this.translate.instant('billing.trialPaymentHeadsUp', {
      date: this.datePipe.transform(this.currentPlan?.subscription?.trialEndDate, 'MMM d, yyyy'),
    });
  }

  get isTrial(): boolean {
    return this.currentPlan?.subscription?.status === 'trial';
  }

  get normalizedPromotionalDiscount(): number {
    if (this.totalsSummary?.discount) return this.totalsSummary.discount;

    const discount = this.currentPlan.paymentModel.discount;
    if (!discount) return 0;

    if (discount.type === 'percentage' || discount.type === 'amount_per_seat') {
      return (discount.value ?? 0) * (this.currentPlan.paymentModel.billableUsers ?? 0);
    }
    return (discount.value ?? 0);
  }

  get promotionalDiscountBadge(): string {
    const discount = this.currentPlan?.paymentModel?.discount;
    if (discount?.type !== 'percentage' || !discount.percentage) {
      return '';
    }

    return this.translate.instant('billing.percentOff', { value: discount.percentage });
  }


  get planPriceDescription(): string {
    const pricePerUser = this.getPricePerUser();
    const userLabel = this.getUserLabel();
    const periodLabel = this.getPeriodLabel();

    return `${pricePerUser}/${userLabel}/${periodLabel}`;
  }

  private getPricePerUser(): string {
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Always respect billing currency if available and has matching price override
    if (billingCurrency && this.currentPlan?.priceOverrides?.length) {
      const matchingPriceOverride = this.currentPlan.priceOverrides.find(
        override => override.currencyCode === billingCurrency,
      );

      if (matchingPriceOverride) {
        // Use the localized price from the price override (convert from cents to dollars)
        const localizedPrice = convertCentsToDollars(parseFloat(matchingPriceOverride.unitPrice));
        return this.localizedPricingService.formatPriceWithCurrency(localizedPrice, billingCurrency, 'excludePlanAndAddon');
      }
    }

    // Priority 2: Use geo-based pricing only if localized pricing is enabled and no billing currency
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && this.currentPlan) {
      // Use the async version for geo-based pricing
      return this.localizedPricingService.formatLocalizedPriceForComponent(
        this.currentPlan?.paymentModel?.priceMonthly || 0,
        this.currentPlan,
        true,
        'excludePlanAndAddon',
      );
    }

    // Priority 3: Fallback to default USD pricing
    const monthlyPrice = normalizePrice(this.currentPlan?.paymentModel?.priceMonthly);
    return this.currencyPipe.transform(monthlyPrice, '$');
  }

  private getUserLabel(): string {
    return this.lowerCasePipe.transform(this.translate.instant('common.user'));
  }

  private getPeriodLabel(): string {
    return this.lowerCasePipe.transform(this.translate.instant('billing.shortMonth'));
  }

  private getUsersCountDescription(): string {
    const freeUsers = this.currentPlan?.freeUsers ?? 0;
    const usersQuantity = this.currentPlan?.billingApiData?.billingDetails?.quantity ?? 0;

    if (freeUsers > 0) {
      const freeUsersText = this.translate.instant('billing.billingFreeUsers', { value: freeUsers });
      return `<br>${usersQuantity} ${freeUsersText.toLowerCase()}`;
    }
    const usersCountText = this.translate.instant('billing.billingUsersCount', { value: usersQuantity });
    return `<br>${usersCountText}`;
  }

  get normalizedPlanName(): string {
    const normalizedPlanId = this.currentPlan?.billingApiData?.billingDetails?.planId?.replace(/_\d+$/, '') || this.currentPlan.productId;

    return this.capitalize.transform(this.translate.instant('billing.' + normalizedPlanId));
  }

  get taxAmount(): number {
    return this.totalsSummary?.tax || this.currentPlan?.paymentModel?.tax || 0;
  }

  get hasOtherItems(): boolean {
    // Check if there are any items to show in the "Other" section
    const hasPromotionalDiscount = !!this.normalizedPromotionalDiscount;
    const hasAnnualDiscount = !!this.annualBillingDiscount;
    const hasTax = this.taxAmount > 0;

    return hasPromotionalDiscount || hasAnnualDiscount || hasTax;
  }

  get totalBillInfo(): string {
    if (!this.currentPlan?.billingApiData?.plans || !this.currentPlan?.billingApiData?.billingDetails?.planId) {
      return '';
    }

    const apiPlan = this.currentPlan.billingApiData.plans.find(
      plan => plan.planId === (this.currentPlan?.planId || this.currentPlan?.billingApiData?.billingDetails?.planId),
    );

    if (!apiPlan) {
      return '';
    }

    const label = this.translate.instant(this.totalBillLabelKey);

    const hasCard = this.currentPlan?.paymentModel?.cardLast4;
    const total = hasCard
      ? (this.currentPlan?.paymentModel?.nextPaymentAmount || this.totalsSummary?.total)
      : (this.totalsSummary?.total || this.currentPlan?.paymentModel?.nextPaymentAmount);

    const normalizedTotal = normalizePrice(total);
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();

    // Priority 1: Always respect billing currency if available
    if (isEnabled && billingCurrency && this.currentPlan) {
      // Use the price directly from backend - no frontend conversion needed
      const formattedPrice = this.localizedPricingService.formatPriceWithCurrency(normalizedTotal, billingCurrency, 'excludePlanAndAddon');
      return `${label}: ${formattedPrice}`;
    }


    // Priority 2: If no card is added, try to get price from price overrides based on user's country
    const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(this.currentPlan, normalizedTotal);
    if (isEnabled && noCardPrice && !billingCurrency) {
      const multiplier = this.currentPlan.paymentModel.paymentRecurrence === 'year' ? 10 : 1;
      const price = (noCardPrice.price * this.currentPlan.paymentModel.billableUsers * multiplier) + (this.totalsSummary?.tax || 0);
      const formattedPrice = this.localizedPricingService.formatPriceWithCurrency(price, noCardPrice.currency, 'excludePlanAndAddon');
      return `${label}: ${formattedPrice}`;
    }

    // Priority 4: Fallback to USD
    const defaultPrice = this.currencyPipe.transform(normalizedTotal, '$');
    return `${label}: ${defaultPrice}`;
  }

  get totalPlanCostWithoutAddonsAndDiscounts(): number {
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Always respect billing currency if available and has matching price override
    if (billingCurrency && this.currentPlan?.priceOverrides?.length) {
      const matchingPriceOverride = this.currentPlan.priceOverrides.find(
        override => override.currencyCode === billingCurrency,
      );

      if (matchingPriceOverride) {
        // Use the localized unit price from the price override (convert from cents to dollars)
        const localizedUnitPrice = convertCentsToDollars(parseFloat(matchingPriceOverride.unitPrice));
        if (this.currentPlan.paymentModel.paymentRecurrence === 'year') {
          return localizedUnitPrice * this.currentPlan.paymentModel.billableUsers * 12;
        }
        return localizedUnitPrice * this.currentPlan.paymentModel.billableUsers;
      }
    }

    // Priority 2: Fallback to default USD pricing
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency) {
      const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(this.currentPlan, this.currentPlan.unitPrice);

      if (noCardPrice) {
        const multiplier = this.currentPlan.paymentModel.paymentRecurrence === 'year' ? 12 : 1;
        return normalizePrice(noCardPrice.price * this.currentPlan.paymentModel.billableUsers * multiplier);
      }
    }

    return normalizePrice(this.currentPlan?.paymentModel?.totalBillingPrice || this.currentPlan.unitPrice);
  }

  /**
   * Formats a price with localized currency for template usage
   * @param price - The price to format
   * @returns string - Formatted price with appropriate currency symbol
   */
  formatLocalizedPrice(price: number): string {
    const normalizedPrice = normalizePrice(price);
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();

    // Priority 1: Always respect billing currency if available
    if (isEnabled && billingCurrency && this.currentPlan) {
      return this.localizedPricingService.formatPriceWithCurrency(normalizedPrice, billingCurrency, 'excludePlanAndAddon');
    }

    // Priority 3: Use geo-based pricing only if localized pricing is enabled and no billing currency
    if (isEnabled && !billingCurrency && this.currentPlan) {
      return this.localizedPricingService.formatLocalizedPriceForComponent(normalizedPrice, this.currentPlan, true, 'excludePlanAndAddon');
    }

    // Priority 4: Fallback to USD
    return this.currencyPipe.transform(normalizedPrice, '$');
  }

  public removeSpaces(text: string): string {
    return text.replace(/\s+/g, '');
  }

  public clearAddonsCache(): void {
    this._cachedAddons = null;
    this._cachedAddonsKey = null;
  }

  public refreshAddonsCache(): void {
    this.updateAddonsCache();
  }

  async ngOnInit() {
    await super.ngOnInit();
    this.updateAddonsCache();
  }

  ngOnDestroy(): void {
    // Clear cached data to prevent memory leaks
    this.clearAddonsCache();
    super.ngOnDestroy();
  }
}
